import React, { useState } from 'react';
import axios from 'axios';

import { useStyles } from '~/assets/styles';
import { FeedbackSnackbar, useSnackbar } from '~/components/core/Molecules/Snackbar';
import { RefreshIcon } from '~/components/icons';
import InlineIconButton from '~/components/InlineIconButton';
import type { ProviderStatusSyncResult } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import TEXTS from './texts';

interface SyncProviderStatusActionContainerProps {
  claimId: number;
  // Absent for a general-expenses payment, which belongs to the claim rather than to an exposure.
  exposureId?: number;
  payableType: string;
  paymentRequestId: number;
  canSyncProviderStatus: boolean;
  onSyncProviderStatus: () => Promise<void> | void;
}

const getSyncProviderStatusUrl = ({
  claimId,
  exposureId,
  payableType,
  paymentRequestId,
}: Pick<SyncProviderStatusActionContainerProps, 'claimId' | 'exposureId' | 'payableType' | 'paymentRequestId'>) =>
  exposureId
    ? `/api/v1/claims/${claimId}/exposures/${exposureId}/${payableType}/payment_requests/${paymentRequestId}/sync_provider_status`
    : `/api/v1/claims/${claimId}/general_expenses/payment_requests/${paymentRequestId}/sync_provider_status`;

const SyncProviderStatusActionContainer: React.FC<SyncProviderStatusActionContainerProps> = ({
  claimId,
  exposureId,
  payableType,
  paymentRequestId,
  canSyncProviderStatus,
  onSyncProviderStatus,
}) => {
  const { classes } = useStyles();
  const [isSyncing, setIsSyncing] = useState(false);
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const { data } = await axios.post<ProviderStatusSyncResult>(
        getSyncProviderStatusUrl({ claimId, exposureId, payableType, paymentRequestId })
      );
      showSnackbar(data.message, data.is_changed ? 'success' : 'info');
      if (data.is_changed) {
        await onSyncProviderStatus();
      }
    } catch (error) {
      reportAxiosError(error);
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <>
      {canSyncProviderStatus && (
        <InlineIconButton
          icon={RefreshIcon}
          className={classes.hoverableNonFilledIcon}
          onClick={handleSync}
          disabled={isSyncing}
          tooltipTitle={TEXTS.syncProviderStatus}
        />
      )}
      {/* Outside the condition: a sync that ends the payment withdraws the action on reload, and the
          answer has to outlive it. */}
      <FeedbackSnackbar state={snackbarState} onClose={closeSnackbar} />
    </>
  );
};

export default SyncProviderStatusActionContainer;
